<script setup lang="ts">
  import moment from "moment";
  import { IndexTable, IndexTableDate, IndexTableEmpty } from "@/components/episto-ui/index-table";
  import type { DataTableColumn } from "@/components/episto-ui/data-table";
  import { StatusBadge } from "@/components/episto-ui/status-badge";
  import MCampaignPushDetail from "@/components/MCampaign/Pushes/MCampaignPushDetail.vue";
  import { M_CAMPAIGN_PUSH_STATUS_VARIANTS } from "@/components/MCampaign/Pushes/mCampaignPushStatus";
  import type { MCampaignPush, MCampaignPushFailure } from "@/types";

  defineProps<{
    pushes: MCampaignPush[];
    failuresByPushId: Record<number, MCampaignPushFailure[]>;
    expandedIds: number[];
    loading: boolean;
  }>();

  const emit = defineEmits<{ toggle: [id: number] }>();

  const columns: DataTableColumn<MCampaignPush>[] = [
    { key: "created_at", label: "Started", width: 170 },
    { key: "status", label: "Status", width: 120 },
    { key: "user_name", label: "Launched by" },
    { key: "duration", label: "Duration", width: 110 },
    { key: "pushed", label: "Pushed", width: 100, align: "right" },
    { key: "retries", label: "Retries", width: 100, align: "right" },
    { key: "failures_count", label: "Errors", width: 100, align: "right" },
  ];

  const sumCounter = (push: MCampaignPush, key: "sync_new" | "retries") =>
    Object.values(push.counters).reduce((sum, counter) => sum + (counter?.[key] ?? 0), 0);

  const duration = (push: MCampaignPush) => {
    if (!push.finished_at) return null;

    const seconds = moment(push.finished_at).diff(moment(push.created_at), "seconds");
    return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  };
</script>

<template>
  <IndexTable
    :items="pushes"
    :columns="columns"
    :expanded-keys="expandedIds"
    :loading="loading"
    :skeleton-rows="5"
    empty-message="This campaign has never been pushed."
    row-data-cy="m-campaign-push-row"
    @row-click="(push) => emit('toggle', push.id)">
    <template #cell-created_at="{ item }">
      <IndexTableDate
        :date="item.created_at"
        format="D MMM YYYY, HH:mm" />
    </template>
    <template #cell-status="{ item }">
      <StatusBadge
        :status="item.status"
        :variants="M_CAMPAIGN_PUSH_STATUS_VARIANTS" />
    </template>
    <template #cell-user_name="{ item }">
      <span :class="item.user_name ? undefined : 'text-muted-foreground'">{{ item.user_name ?? "System" }}</span>
    </template>
    <template #cell-duration="{ item }">
      <span
        v-if="duration(item)"
        class="text-muted-foreground">
        {{ duration(item) }}
      </span>
      <IndexTableEmpty v-else />
    </template>
    <template #cell-pushed="{ item }">
      {{ sumCounter(item, "sync_new") }}
    </template>
    <template #cell-retries="{ item }">
      {{ sumCounter(item, "retries") }}
    </template>
    <template #cell-failures_count="{ item }">
      <span :class="item.failures_count > 0 ? 'font-medium text-status-error-strong' : 'text-muted-foreground'">
        {{ item.failures_count }}
      </span>
    </template>
    <template #expanded="{ item }">
      <MCampaignPushDetail
        :push="item"
        :failures="failuresByPushId[item.id]" />
    </template>
  </IndexTable>
</template>
